import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './app';
import { RecoilRoot } from 'recoil';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// disable console log in production mode
if (import.meta.env.MODE !== 'development') {
    console.log = () => {};
}

// initialize sentry (production only). The SDK is ~0.4 MB, so it loads after the first screen instead of with the
// main bundle. Errors raised before it arrives are kept here and reported once it is ready.
if (import.meta.env.PROD) {
    const early = [];
    const keep = (ev) => {
        const err = ev.error ?? ev.reason;
        if (err) early.push(err);
    };
    window.addEventListener('error', keep);
    window.addEventListener('unhandledrejection', keep);

    const startSentry = () =>
        import('@sentry/react')
            .then(({ init, captureException, lazyLoadIntegration, addIntegration }) => {
                init({
                    dsn: 'https://cd26efb6d7c20e1247cff042d5240142@o4511816172175360.ingest.us.sentry.io/4511816190394368',
                    tracesSampleRate: 0,
                    replaysSessionSampleRate: 0,
                    replaysOnErrorSampleRate: 1,
                    ignoreErrors: [/^Error: int64$/],
                    denyUrls: [/pagead/, /googlesyndication/, /doubleclick/],
                });
                window.removeEventListener('error', keep);
                window.removeEventListener('unhandledrejection', keep);
                early.splice(0).forEach((err) => captureException(err));
                // Replay is ~0.5 MB, so it comes from the Sentry CDN.
                lazyLoadIntegration('replayIntegration')
                    .then((replayIntegration) => addIntegration(replayIntegration()))
                    .catch(() => {});
            })
            .catch(() => {});

    // After the first screen (window.appReady, index.html), so it does not compete with it.
    (window.appReady ?? Promise.resolve()).then(startSentry);
}

// Reload when a stale deployment's chunk is gone (server falls back to index.html). Once per minute at most, so a
// chunk that stays missing cannot loop, while a later deployment in the same tab still gets its own reload.
window.addEventListener('vite:preloadError', () => {
    const last = Number(sessionStorage.getItem('chunk-reloaded') || 0);
    if (Date.now() - last > 60 * 1000) {
        sessionStorage.setItem('chunk-reloaded', String(Date.now()));
        window.location.reload();
    }
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <RecoilRoot>
        <App />
    </RecoilRoot>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();
